import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { enumValueLabel } from 'utils/auditLabels';
import Pill from './Pill';

/** Above this length (or with a line break) a value gets the 2-line clamp + toggle (design §5.3). */
const LONG_TEXT_THRESHOLD = 140;
const isLongText = (value: unknown): value is string =>
  typeof value === 'string' && (value.length > LONG_TEXT_THRESHOLD || value.includes('\n'));

const displayScalar = (t: TFunction, field: string, value: unknown): string =>
  value === undefined || value === null || value === '' ? '—' : enumValueLabel(t, field, String(value));

interface ValueCellProps {
  field: string;
  value: unknown;
  bold?: boolean;
  strike?: boolean;
}

/** The value half of a row — before *or* after — built once and reused by both the diff and the
 * creation branch (which only ever renders the "after" side). */
const ValueCell = ({ field, value, bold, strike }: ValueCellProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (Array.isArray(value)) {
    const items = value.map(String);
    if (items.length === 0) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="flex flex-wrap gap-1.5">
        {items.map((item, index) => (
          <Pill key={`${item}-${index}`} tone="neutral">
            {enumValueLabel(t, field, item)}
          </Pill>
        ))}
      </span>
    );
  }

  if (isLongText(value)) {
    return (
      <span className="flex flex-col items-start gap-1">
        <span
          className={cn(
            'whitespace-pre-wrap [overflow-wrap:anywhere]',
            bold && 'font-semibold',
            strike && 'text-muted-foreground line-through',
            !expanded && 'line-clamp-2'
          )}
        >
          {value}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="text-[12px] text-muted-foreground underline hover:text-foreground"
        >
          {expanded ? t('Show less') : t('Show all')}
        </button>
      </span>
    );
  }

  return (
    <span className={cn('[overflow-wrap:anywhere]', bold && 'font-semibold', strike && 'text-muted-foreground line-through')}>
      {displayScalar(t, field, value)}
    </span>
  );
};

export default ValueCell;
